import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner, Table } from "react-bootstrap";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { getJobListing } from "../../connections/getJobListing";
import { getCheckUploadStatus } from "../../connections/getCheckUploadStatus";
import { propTypes } from "react-bootstrap/esm/Image";

const JobStatusTable = ({
  token,
  jobId,
  title = "Recent Job Requests",
  type,
}) => {
  const [jobs, setJobs] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingJobId, setLoadingJobId] = useState(null);

  const fetchJobs = async () => {
    try {
      setLoadingList(true);
      const data = await getJobListing(token, type);
      const jobRequests = data?.payload?.job_requests || [];
      setJobs(jobRequests);
    } catch (error) {
      toast.error("Failed to fetch job listings");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (token) fetchJobs();
  }, [token]);

  // ✅ When new upload returns jobId, refresh list so user sees it immediately
  useEffect(() => {
    if (jobId) fetchJobs();
  }, [jobId]);

  const checkJobStatus = async (id) => {
    try {
      setLoadingJobId(id);
      const statusRes = await getCheckUploadStatus(token, id);

      if (statusRes?.status) {
        setJobs((prev) =>
          prev.map((item) =>
            item.job_id === id
              ? { ...item, job_status: statusRes.status }
              : item,
          ),
        );
      } else {
        toast.error("Failed to fetch job status");
      }
    } catch (error) {
      toast.error("Something went wrong while checking job status");
    } finally {
      setLoadingJobId(null);
    }
  };

  // ✅ sort latest first (if backend gives timestamps)
  const sortedJobs = useMemo(() => {
    const copy = [...(jobs || [])];

    // try best effort sorting
    return copy.sort((a, b) => {
      const aT = new Date(a.updated_at || a.created_at || 0).getTime();
      const bT = new Date(b.updated_at || b.created_at || 0).getTime();
      return bT - aT;
    });
  }, [jobs]);

  return (
    <div className="mt-4 p-3 border rounded bg-white">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div>
          <h5 className="mb-0">{title}</h5>
          <small className="text-muted">
            Latest jobs show here. Use “Check” to get the current status.
          </small>
        </div>

        <Button
          variant="outline-primary"
          onClick={fetchJobs}
          disabled={loadingList}
        >
          {loadingList ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      {loadingList ? (
        <div className="py-3">Loading job data...</div>
      ) : sortedJobs?.length > 0 ? (
        <Table striped bordered hover responsive className="mb-0">
          <thead>
            <tr>
              <th style={{ width: 220 }}>Request Type</th>
              <th style={{ width: 220 }}>Job ID</th>
              <th style={{ width: 180 }}>Last Known Status</th>
              <th style={{ width: 220 }}>Action</th>
              {/* <th style={{ width: 220 }}>Updated</th> */}
            </tr>
          </thead>

          <tbody>
            {sortedJobs.map((job) => (
              <tr key={job?.id || job?.job_id}>
                <td>{job?.request_type?.trim() ? job.request_type : "-"}</td>
                <td>{job?.job_id || "-"}</td>

                {/* backend me job.status vs job.job_status confusion tha, so display best available */}
                <td>{job?.job_status || job?.status || "-"}</td>

                <td>
                  {job?.job_status === "done" ? (
                    "done"
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() => checkJobStatus(job.job_id)}
                      disabled={loadingJobId === job.job_id}
                    >
                      {loadingJobId === job.job_id ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />{" "}
                          Checking...
                        </>
                      ) : (
                        "Check"
                      )}
                    </Button>
                  )}
                </td>

                {/* <td>
                  {job?.updated_at || job?.created_at
                    ? new Date(
                        job.updated_at || job.created_at,
                      ).toLocaleString()
                    : "-"}
                </td> */}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div className="py-3 text-muted">No job records found.</div>
      )}
    </div>
  );
};

JobStatusTable.propTypes = {
  token: PropTypes.string.isRequired,
  jobId: PropTypes.any,
  title: PropTypes.string,
  type: propTypes?.string,
};

export default JobStatusTable;
